import api from './api';
import { normalizeLanguage } from '../constants/Languages';

const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue !== 'string') {
      return accumulator;
    }

    accumulator[normalizeLanguage(key)] = entryValue;
    return accumulator;
  }, {});
};

const normalizePlan = (plan) => ({
  id: plan.id,
  storeId: plan.storeId,
  name: normalizeLocalizedDictionary(plan.name),
  price: plan.price,
  currency: plan.currency,
  billingInterval: plan.billingInterval,
  entitlementType: plan.entitlementType,
  allowanceQuantity: plan.allowanceQuantity,
  allowCustomerCancellation: plan.allowCustomerCancellation,
  commitmentMonths: plan.commitmentMonths,
  serviceIds: plan.serviceIds || [],
  teamMemberIds: Array.isArray(plan.teamMemberIds) ? plan.teamMemberIds.map(String) : [],
});

const normalizeEntitlementUsage = (usage) => {
  if (!usage) {
    return null;
  }

  return {
    entitlementType: usage.entitlementType,
    allowanceQuantity: usage.allowanceQuantity,
    usedQuantity: usage.usedQuantity ?? 0,
    remainingQuantity: usage.remainingQuantity,
    coveredServiceIds: usage.coveredServiceIds || [],
  };
};

const normalizeMembership = (membership) => ({
  id: membership.id,
  storeId: membership.storeId,
  membershipPlanId: membership.membershipPlanId,
  status: membership.status,
  currentPeriodStartUtc: membership.currentPeriodStartUtc,
  currentPeriodEndUtc: membership.currentPeriodEndUtc,
  startedAtUtc: membership.startedAtUtc,
  manuallyPaidThroughUtc: membership.manuallyPaidThroughUtc ?? null,
  cancelAtPeriodEnd: membership.cancelAtPeriodEnd,
  cancelsAtUtc: membership.cancelsAtUtc,
  isPaused: Boolean(membership.isPaused),
  entitlementUsage: normalizeEntitlementUsage(membership.entitlementUsage),
});

const normalizeBookingEntitlement = (entitlement) => ({
  customerMembershipId: entitlement.customerMembershipId,
  membershipPlanId: entitlement.membershipPlanId,
  planName: normalizeLocalizedDictionary(entitlement.planName),
  entitlementType: entitlement.entitlementType,
  allowanceQuantity: entitlement.allowanceQuantity,
  usedQuantity: entitlement.usedQuantity ?? 0,
  remainingQuantity: entitlement.remainingQuantity,
  requiredUses: entitlement.requiredUses ?? 0,
  canCoverAllServices: Boolean(entitlement.canCoverAllServices),
  canRedeem: Boolean(entitlement.canRedeem),
  coveredServiceIds: entitlement.coveredServiceIds || [],
  requiredServiceIds: entitlement.requiredServiceIds || [],
  missingServiceIds: entitlement.missingServiceIds || [],
  allowedTeamMemberIds: Array.isArray(entitlement.allowedTeamMemberIds) ? entitlement.allowedTeamMemberIds.map(String) : [],
});

const withCustomerStore = (storeId) => ({
  _authContext: 'customer',
  _storeId: storeId,
});

// Whether this store lets the signed-in customer pay online at all. A store can take a given
// customer's payments in store only, whatever it offers everyone else.
export const getMyPaymentOptions = async (storeId) => {
  const response = await api.get('/customers/me/payment-options', withCustomerStore(storeId));
  return {
    onlinePaymentsAllowed: (response.data?.onlinePaymentsAllowed ?? response.data?.OnlinePaymentsAllowed) !== false,
  };
};

export const getStoreMembershipPlans = async (storeId) => {
  const response = await api.get('/membership-plans/catalog', withCustomerStore(storeId));

  return (response.data || []).map(normalizePlan);
};

export const getCustomerMemberships = async (storeId) => {
  const response = await api.get('/membership-plans/customer-memberships', withCustomerStore(storeId));

  return (response.data || []).map(normalizeMembership);
};

export const createMembershipCheckoutSession = async (storeId, planId) => {
  const response = await api.post(
    `/membership-plans/${planId}/checkout-session`,
    null,
    withCustomerStore(storeId),
  );

  return {
    clientSecret: response.data?.clientSecret ?? response.data?.ClientSecret ?? null,
    // Direct charges live on the store's Stripe account; Stripe.js must be opened on it.
    stripeAccountId: response.data?.stripeAccountId ?? response.data?.StripeAccountId ?? null,
  };
};

export const getBookingMembershipEntitlements = async (storeId, serviceIds) => {
  const response = await api.post(
    '/membership-plans/booking-entitlements',
    { serviceIds },
    withCustomerStore(storeId),
  );

  return (response.data || []).map(normalizeBookingEntitlement);
};

export const getManagementBookingMembershipEntitlements = async (storeId, customerId, serviceIds) => {
  const response = await api.post(
    '/membership-plans/management/booking-entitlements',
    { customerId, serviceIds },
    {
      _authContext: 'management',
      _storeId: storeId,
    },
  );

  return (response.data || []).map(normalizeBookingEntitlement);
};

export const cancelCustomerMembership = async (storeId, planId) => {
  const response = await api.post(
    `/membership-plans/${planId}/cancel`,
    null,
    withCustomerStore(storeId),
  );

  return normalizeMembership(response.data);
};

export const pauseCustomerMembership = async (storeId, planId) => {
  const response = await api.post(
    `/membership-plans/${planId}/pause`,
    null,
    withCustomerStore(storeId),
  );

  return normalizeMembership(response.data);
};

export const resumeCustomerMembershipFromPause = async (storeId, planId) => {
  const response = await api.post(
    `/membership-plans/${planId}/resume`,
    null,
    withCustomerStore(storeId),
  );

  return normalizeMembership(response.data);
};

export const resumeCustomerMembershipCancellation = async (storeId, planId) => {
  const response = await api.post(
    `/membership-plans/${planId}/resume-cancellation`,
    null,
    withCustomerStore(storeId),
  );

  return normalizeMembership(response.data);
};
